import { useMemo, useState, type ReactNode } from 'react'
import { Item } from '@react-stately/collections'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useDebounce } from 'hooks/useDebounce'
import { useSearchLocations } from 'hooks/queries/externalIntegrations/useSearchLocations'
import { useValidateLocation } from 'hooks/queries/externalIntegrations/useValidateLocation'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostLocation } from 'types'
import Styles from './EditCaptionModal.module.scss'
import { Marker2 } from 'blaze-ui/icons'
import X02 from 'blaze-ui/icons/16/X02'
import { Button } from 'blaze-ui/Button'

interface LocationPickerProps {
  integrationId: number
  provider: ExternalIntegrationEnum
  location: PostLocation | null
  onLocationChange: (location: PostLocation | null) => void
  disabled?: boolean
}

const VALIDATABLE_PROVIDERS = new Set([ExternalIntegrationEnum.LinkedIn])

export const LocationPicker = ({
  integrationId,
  provider,
  location,
  onLocationChange,
  disabled,
}: LocationPickerProps) => {
  const [query, setQuery] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const debouncedQuery = useDebounce(query, 200)
  const { data: locations = [], isLoading, isError, isAuthError } = useSearchLocations({
    integrationId,
    query: debouncedQuery,
  })

  const shouldValidate = VALIDATABLE_PROVIDERS.has(provider) && !!location
  const { data: validation, isLoading: isValidating } = useValidateLocation({
    integrationId: shouldValidate ? integrationId : undefined,
    geoUrn: shouldValidate ? location?.id : undefined,
  })

  const renderValidation = useMemo(() => {
    if (!shouldValidate) return null
    let content = null

    if (isValidating) {
      content = (
        <span className={Styles.locationValidating} aria-label="Validating location">
          ...
        </span>
      )
    } else if (validation && !validation.meets_minimum) {
      content = (
        <Tooltip
          label="This location may not have enough followers for targeted delivery. The post will still be published without location targeting."
          placement="top"
        >
          <span className={Styles.locationWarning} aria-label="Audience too small">
            ⚠
          </span>
        </Tooltip>
      )
    } else if (validation?.meets_minimum) {
      content = (
        <span className={Styles.locationValid} aria-label="Location validated">
          ✓
        </span>
      )
    }
    return <span className={Styles.locationValidation}>{content}</span>
  }, [shouldValidate, isValidating, validation])

  if (location) {
    return (
      <div className={Styles.locationSelected}>
        <Marker2 size={16} aria-hidden />
        <span className={Styles.locationLabel}>{location.full_name || location.name}</span>
        {renderValidation}
        <button
          className={Styles.locationRemove}
          onClick={() => {
            onLocationChange(null)
            setIsAdding(false)
          }}
          disabled={disabled}
          aria-label="Remove location"
          type="button"
        >
          <X02 size={12} aria-hidden />
        </button>
      </div>
    )
  }

  const isSearchPending = query.length >= 2 && !isError && (debouncedQuery !== query || isLoading)

  let noResultsMessage: ReactNode | undefined
  if (isAuthError && query.length >= 2) {
    noResultsMessage = (
      <div className={Styles.locationMessage}>Account disconnected. Please reconnect to search locations.</div>
    )
  } else if (isError && query.length >= 2) {
    noResultsMessage = <div className={Styles.locationMessage}>Unable to search locations. Please try again.</div>
  } else if (isSearchPending) {
    noResultsMessage = <div className={Styles.locationMessage}>Searching...</div>
  } else if (query.length >= 2) {
    noResultsMessage = <div className={Styles.locationMessage}>No locations found</div>
  }

  if (!isAdding) {
    return (
      <Button frontIcon={Marker2} variant="secondary" size="md" onPress={() => setIsAdding(true)} isDisabled={disabled}>
        Add Location
      </Button>
    )
  }

  return (
    <ComboBox
      aria-label="Search location"
      autoFocus
      placeholder="Search city, state, country..."
      inputValue={query}
      onInputChange={setQuery}
      onSelectionChange={(key) => {
        const selected = locations.find((loc) => loc.id === key)
        if (selected) {
          onLocationChange(selected)
          setQuery('')
        }
      }}
      onBlur={() => {
        setIsAdding(false)
        setQuery('')
      }}
      defaultFilter={() => true}
      size="sm"
      contentsClassName={Styles.locationComboBox}
      inputClassName={Styles.locationInput}
      optionClassName={Styles.locationOption}
      chevronSize={0}
      popoverClassName={Styles.locationPopover}
      popoverMaxHeight={220}
      isVirtualized={false}
      disabled={disabled}
      items={query.length >= 2 ? locations.map((loc) => ({ id: loc.id, name: loc.full_name || loc.name })) : []}
      noResultsMessage={noResultsMessage}
    >
      {(item) => <Item>{item.name}</Item>}
    </ComboBox>
  )
}
